import React, { useState } from 'react';
import { Plus, Power } from 'lucide-react';
import { useLanguage } from '../../contexts/LanguageContext';
import { shiftTemplatesAPI } from '../../services/api';
import { notify } from '../../utils/notify';
import Modal from '../../components/Modal';
import StatusBadge from '../../components/StatusBadge';

// Shift templates ("Day 06:00–18:00"), for every site or one. They only
// fill the times of a new shift; turning one off leaves its shifts alone.

const EMPTY = { name: '', site_id: '', start_time: '06:00', end_time: '18:00' };

const TemplatesDialog = ({ templates, sites, canCreate, canEdit, onClose, onChanged }) => {
  const { t } = useLanguage();
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);

  const save = async (e) => {
    e.preventDefault();
    try {
      setSaving(true);
      await shiftTemplatesAPI.create({ ...form, site_id: form.site_id ? Number(form.site_id) : null });
      notify(t('Template created'), 'success');
      setForm(null);
      onChanged();
    } catch (error) {
      const errors = error.response?.data?.errors;
      notify(errors ? Object.entries(errors).map(([k, v]) => `${k}: ${v}`).join('; ') : (error.response?.data?.message || t('Error saving the template')), 'error');
    } finally {
      setSaving(false);
    }
  };

  const setActive = async (template, active) => {
    try {
      await shiftTemplatesAPI.update(template.id, { active });
      notify(active ? t('Template activated') : t('Template deactivated'), 'success');
      onChanged();
    } catch (error) {
      notify(error.response?.data?.message || t('Error saving the template'), 'error');
    }
  };

  return (
    <Modal open title={t('Shift Templates')} subtitle={t('They fill in the times of a new shift')} onClose={onClose} busy={saving} size="md">
      {templates.length === 0 ? (
        <p className="sh-muted">{t('No templates yet')}</p>
      ) : (
        <ul className="sh-templates">
          {templates.map((x) => (
            <li key={x.id} className="sh-template">
              <span className="sh-template__name">
                <strong>{x.name}</strong>
                <span className="sh-muted">{x.start_time}–{x.end_time} · {x.site_name || t('Every site')}</span>
              </span>
              <StatusBadge status={x.active ? 'active' : 'inactive'}>{t(x.active ? 'Active' : 'Inactive')}</StatusBadge>
              {canEdit && (
                <button type="button" className="btn btn--secondary btn--icon"
                  title={x.active ? t('Deactivate template') : t('Activate template')}
                  aria-label={x.active ? t('Deactivate template') : t('Activate template')}
                  onClick={() => setActive(x, !x.active)}>
                  <Power aria-hidden="true" />
                </button>
              )}
            </li>
          ))}
        </ul>
      )}

      {canCreate && !form && (
        <button type="button" className="btn btn--secondary" onClick={() => setForm({ ...EMPTY })}>
          <Plus aria-hidden="true" /> {t('New Template')}
        </button>
      )}
      {form && (
        <form className="sh-dialog sh-template-form" onSubmit={save}>
          <div className="sh-form-row">
            <div className="sh-field">
              <label className="field-label" htmlFor="template-name">{t('Name *')}</label>
              <input id="template-name" className="input" value={form.name} maxLength={100} required
                onChange={(e) => setForm({ ...form, name: e.target.value })} />
            </div>
            <div className="sh-field">
              <label className="field-label" htmlFor="template-site">{t('Site')}</label>
              <select id="template-site" className="select" value={form.site_id} onChange={(e) => setForm({ ...form, site_id: e.target.value })}>
                <option value="">{t('Every site')}</option>
                {sites.filter((s) => s.active).map((s) => <option key={s.id} value={s.id}>{s.client_name} – {s.name}</option>)}
              </select>
            </div>
          </div>
          <div className="sh-form-row">
            <div className="sh-field">
              <label className="field-label" htmlFor="template-start">{t('Start')}</label>
              <input id="template-start" className="input" type="time" value={form.start_time} required
                onChange={(e) => setForm({ ...form, start_time: e.target.value })} />
            </div>
            <div className="sh-field">
              <label className="field-label" htmlFor="template-end">{t('End')}</label>
              <input id="template-end" className="input" type="time" value={form.end_time} required
                onChange={(e) => setForm({ ...form, end_time: e.target.value })} />
            </div>
          </div>
          {form.end_time && form.end_time <= form.start_time && <p className="field-hint">{t('Ends the next day')}</p>}
          <div className="sh-form-actions">
            <button type="button" className="btn btn--secondary" onClick={() => setForm(null)}>{t('Cancel')}</button>
            <button type="submit" className="btn btn--primary" disabled={saving}>{saving ? t('Saving...') : t('Save')}</button>
          </div>
        </form>
      )}
    </Modal>
  );
};

export default TemplatesDialog;
